/**
 * Account recovery — get a workforce user out of a state they cannot leave
 * on their own. The web face of docs/operations/ACCOUNT_RECOVERY.md.
 *
 * Security owner's ruling (2026-09-16): system administrators only (the
 * server checks SYSTEM_USER_MANAGE); resetting an authenticator or restarting
 * setup asks for your own current authenticator code and a reason; you cannot
 * do either to your own account. No code or secret is ever shown here —
 * codes go to the person's work email.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiRequest } from '../../../services/api';
import { STEP_UP_SSO_REQUIRED, useSsoConfig } from '../../../services/ssoApi';
import ConfirmWithMicrosoft from '../../../components/Auth/ConfirmWithMicrosoft';
import { Dialog, DialogContent, DialogDescription, DialogTitle, useReturnFocus } from '../../../components/shadcn/ui/dialog';

type State = 'ACTIVE' | 'DEACTIVATED' | 'PENDING_INVITE' | 'OTP_ISSUED' | 'STUCK_AFTER_PASSWORD' | 'AUTHENTICATOR_RESET';
type CodeStatus = 'LIVE' | 'USED' | 'EXPIRED' | 'NONE' | null;
export type Account = {
  id: string; email: string; name: string | null; state: State;
  hasAuthenticator: boolean; backupCodesLeft: number; backupCodesLocked?: boolean;
  lastSignIn: string | null; code: CodeStatus; codeExpiresAt: string | null;
};
type Payload = { you: string; accounts: Account[] };
type Action = 'send-code' | 'reset-authenticator' | 'restart-setup';

const STATE: Record<State, { label: string; tone: string; hint: string }> = {
  ACTIVE: { label: 'Active', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200', hint: 'Can sign in.' },
  DEACTIVATED: { label: 'Deactivated', tone: 'bg-slate-100 text-slate-700 border-slate-300', hint: 'Switched off. Recovery does not apply.' },
  PENDING_INVITE: { label: 'Not invited', tone: 'bg-slate-50 text-slate-700 border-slate-200', hint: 'Has never been sent a setup code.' },
  OTP_ISSUED: { label: 'Setting up', tone: 'bg-sky-50 text-sky-800 border-sky-200', hint: 'Waiting on the setup code.' },
  STUCK_AFTER_PASSWORD: { label: 'Stuck after password', tone: 'bg-amber-50 text-amber-800 border-amber-200', hint: 'Password saved, authenticator never registered.' },
  AUTHENTICATOR_RESET: { label: 'Authenticator reset', tone: 'bg-amber-50 text-amber-800 border-amber-200', hint: 'Needs to register a new phone.' },
};

const CODE_LABEL: Record<Exclude<CodeStatus, null>, string> = {
  LIVE: 'code sent', USED: 'code used', EXPIRED: 'code expired', NONE: '—',
};

const ACTION: Record<Action, { title: string; button: string; explains: string; stepUp: boolean }> = {
  'send-code': {
    title: 'Send a setup code',
    button: 'Send code',
    explains: 'Emails a new code. Any earlier code stops working. If they already chose a password, the code resumes at the authenticator step and asks for that password.',
    stepUp: false,
  },
  'reset-authenticator': {
    title: 'Reset authenticator',
    button: 'Reset and email',
    explains: 'For a lost or replaced phone. Removes their authenticator, signs them out everywhere, keeps their password and access, and emails a code to register a new phone (it needs their password).',
    stepUp: true,
  },
  'restart-setup': {
    title: 'Restart setup',
    button: 'Restart and email',
    explains: 'Last resort, for a lost phone AND a forgotten password. Returns the account to the start: new password, new authenticator. It rests on their mailbox alone, so confirm who is asking first.',
    stepUp: true,
  },
};

/** Which actions make sense for a row. The server enforces the same rules. */
export function actionsFor(a: Account, you: string): Action[] {
  if (a.state === 'DEACTIVATED') return [];
  const self = a.id === you;
  const out: Action[] = [];
  if (['PENDING_INVITE', 'OTP_ISSUED', 'STUCK_AFTER_PASSWORD', 'AUTHENTICATOR_RESET'].includes(a.state)) out.push('send-code');
  if (!self && a.state === 'ACTIVE') out.push('reset-authenticator');
  if (!self && a.state !== 'PENDING_INVITE') out.push('restart-setup');
  return out;
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never');

const AccountRecovery: React.FC = () => {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<{ account: Account; action: Action } | null>(null);
  const [reason, setReason] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const returnFocus = useReturnFocus(open !== null);
  // The server said this administrator confirms with Microsoft, not an authenticator code.
  const [needsMicrosoft, setNeedsMicrosoft] = useState(false);
  // With Microsoft sign-in on, an administrator who signs in with Microsoft has no authenticator
  // code to type, so the code is optional here; the server decides which confirmation applies.
  const ssoOn = Boolean(useSsoConfig());

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const r = await apiRequest<Payload>('GET', '/admin/accounts');
    if (r.success && r.data) { setData(r.data); setLoading(false); return; }
    setData(null);
    setLoadError(r.status === undefined
      ? 'Could not reach the server, so nothing below is current.'
      : r.status === 403
        ? 'Account recovery is for system administrators. Your account does not hold that role.'
        : (r.message || 'Accounts could not be loaded.'));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = data?.accounts || [];
    return q ? list.filter((a) => a.email.toLowerCase().includes(q) || (a.name || '').toLowerCase().includes(q)) : list;
  }, [data, filter]);

  const needsHelp = rows.filter((a) => ['STUCK_AFTER_PASSWORD', 'AUTHENTICATOR_RESET'].includes(a.state) || a.code === 'USED' || a.code === 'EXPIRED' || a.backupCodesLocked).length;

  const start = (account: Account, action: Action) => {
    setOpen({ account, action }); setReason(''); setTotp(''); setDialogError(''); setNeedsMicrosoft(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!open) return;
    setBusy(true); setDialogError(''); setNeedsMicrosoft(false);
    const body = ACTION[open.action].stepUp ? { reason: reason.trim(), totpCode: totp } : {};
    const r = await apiRequest<{ email: string; delivered: boolean; error: string | null }>(
      'POST', `/admin/accounts/${open.account.id}/${open.action}`, body
    );
    setBusy(false);
    if (!r.success || !r.data) {
      if (r.code === STEP_UP_SSO_REQUIRED) {
        setNeedsMicrosoft(true);
        setDialogError(r.message || 'Confirm this with Microsoft first, then do it again.');
        return;
      }
      setDialogError(r.message || 'The server refused this.');
      return;
    }
    if (r.data.delivered) toast.success(`Code emailed to ${r.data.email}.`);
    else toast.error(`Done, but the email to ${r.data.email} was not delivered: ${r.data.error || 'unknown reason'}. Send the code again once that is fixed.`);
    setOpen(null);
    await load();
  };

  if (loading && !data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" /></div>;

  return (
    <div className="space-y-6 min-w-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424] flex items-center gap-2"><KeyRound className="h-5 w-5" /> Account recovery</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Workforce sign-in state. Before resetting or restarting anyone, confirm who is asking by phone, Teams or in person — never by email alone.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data && <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800">{needsHelp} may need help</span>}
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-6 text-center text-rose-700" role="alert">
          {loadError}
          <div className="mt-3"><button type="button" onClick={load} className="rounded border border-rose-300 bg-white px-3 py-1 text-sm text-rose-800">Try again</button></div>
        </div>
      )}

      {data && (
        <section className="rounded-lg border border-black/10 bg-white">
          <header className="flex flex-col gap-2 border-b border-black/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-[#242424]">Accounts <span className="ml-1 text-sm font-normal text-muted-foreground">{rows.length}</span></h2>
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">Find an account</span>
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Name or email"
                className="h-9 w-full rounded-md border border-gray-300 pl-8 pr-3 text-sm" />
            </label>
          </header>
          {rows.length === 0 ? <p className="px-4 py-6 text-sm text-muted-foreground">No accounts match.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-4 py-2">Person</th><th className="px-4 py-2">State</th><th className="px-4 py-2">Authenticator</th>
                    <th className="px-4 py-2 text-right">Backup codes</th><th className="px-4 py-2">Setup code</th><th className="px-4 py-2">Last sign-in</th><th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const s = STATE[a.state];
                    const acts = actionsFor(a, data.you);
                    return (
                      <tr key={a.id} className="border-t border-black/5 align-top">
                        <td className="px-4 py-2">
                          <div className="font-medium text-[#242424]">{a.name || a.email}{a.id === data.you && <span className="ml-1 text-xs font-normal text-muted-foreground">(you)</span>}</div>
                          {a.name && <div className="text-xs text-muted-foreground">{a.email}</div>}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${s.tone}`}>{s.label}</span>
                          <div className="mt-1 text-xs text-muted-foreground">{s.hint}</div>
                        </td>
                        <td className="px-4 py-2 text-xs">{a.hasAuthenticator ? 'Registered' : 'None'}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-xs">
                          {a.state === 'ACTIVE' ? a.backupCodesLeft : '—'}
                          {a.backupCodesLocked && <div className="text-amber-800">locked after failed tries</div>}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {a.code ? CODE_LABEL[a.code] : '—'}
                          {a.code === 'LIVE' && a.codeExpiresAt && <div className="text-muted-foreground">until {when(a.codeExpiresAt)}</div>}
                        </td>
                        <td className="px-4 py-2 text-xs">{when(a.lastSignIn)}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-2">
                            {acts.length === 0 && <span className="text-xs text-muted-foreground">{a.id === data.you ? 'Ask another administrator' : '—'}</span>}
                            {acts.map((act) => (
                              <button key={act} type="button" onClick={() => start(a, act)}
                                className={act === 'restart-setup'
                                  ? 'rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs text-rose-800 hover:bg-rose-50'
                                  : 'rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs hover:bg-gray-50'}>
                                {ACTION[act].title}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {open && (
        // A true modal (shadcn Dialog): focus trapped and returned to the row's button, Escape closes, the page is
        // inert. Escape and Cancel close it (not while the act is in flight); a stray click outside does not, so a typed
        // reason is not lost.
        <Dialog open onOpenChange={(o) => { if (!o && !busy) setOpen(null); }}>
          <DialogContent
            hideClose
            onCloseAutoFocus={returnFocus}
            className="max-w-md rounded-lg bg-white p-5 shadow-xl"
            onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
          <form onSubmit={submit} className="w-full space-y-4">
            <div>
              <DialogTitle className="text-lg font-semibold leading-normal tracking-normal text-[#242424]">{ACTION[open.action].title}</DialogTitle>
              <p className="text-sm text-[#242424]">{open.account.name || open.account.email} <span className="text-muted-foreground">· {open.account.email}</span></p>
            </div>
            <DialogDescription className="text-sm text-gray-700">{ACTION[open.action].explains}</DialogDescription>
            {ACTION[open.action].stepUp && (
              <>
                <label className="block text-sm font-medium">
                  Reason, and how you confirmed who asked
                  <textarea required minLength={8} value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder="Replaced phone — confirmed on a Teams video call"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm font-medium">
                  Your own authenticator code{ssoOn ? ' (leave empty if you sign in with Microsoft)' : ''}
                  <input required={!ssoOn} value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                    className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 font-mono tracking-[0.3em]" />
                </label>
              </>
            )}
            {dialogError && <p role="alert" className="text-sm text-rose-700">{dialogError}</p>}
            {needsMicrosoft && (
              <ConfirmWithMicrosoft
                purpose="ADMIN_RECOVERY"
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-60"
              />
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(null)} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm">Cancel</button>
              <button type="submit"
                disabled={busy || (ACTION[open.action].stepUp && (reason.trim().length < 8 || (totp.length !== 6 && !(ssoOn && totp.length === 0))))}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-white disabled:opacity-60 ${open.action === 'restart-setup' ? 'bg-rose-700' : 'bg-[#242424]'}`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {ACTION[open.action].button}
              </button>
            </div>
          </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AccountRecovery;
